<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { useCustomizerStore } from '@/stores/customizer';
import { useAuthStore } from '@/stores/auth';
import Logo from '/public/images/logos/snapei-b2b-logo.svg';
import LogoLight from '/public/images/logos/snapei-b2b-logo-light.svg';

const customizer = useCustomizerStore();
const authStore = useAuthStore();
const { mobile } = useDisplay();
const isDarkTheme = computed(() => customizer.actTheme === 'DARK_ORANGE_THEME');

// Mismas rutas que components/lc/Full/vertical-sidebar/sidebarItem.ts (menú
// lateral de escritorio) — se repiten aquí porque el menú inferior móvil
// necesita sus propios íconos mdi-* (v-bottom-navigation, no tabler-icons).
const tabs = computed(() => [
    { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/tat/admin/Dashboard' },
    { title: 'Configuración', icon: 'mdi-cog', to: '/tat/admin/Asignaciones' },
    { title: 'Pedidos', icon: 'mdi-receipt-text', to: '/tat/admin/Pedidos' },
    ...(authStore.isSuperUser
        ? [{ title: 'Centros', icon: 'mdi-warehouse', to: '/tat/admin/CentrosOperativos' }]
        : []),
]);

const route = useRoute();
const activeTab = computed(
    () => tabs.value.find((tab) => tab.to === route.path)?.to ?? tabs.value[0]?.to,
);

// Ver TatSeller.vue: el menú inferior usa el sistema de "layout items" de
// Vuetify para su posición fija, que en cargas iniciales en viewports
// angostos queda desfasado — se fuerza un resize tras montar para que
// recalcule.
onMounted(() => {
    [0, 150, 500].forEach((delay) => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), delay);
    });
});
</script>

<template>
  <v-locale-provider>
    <v-app
      :theme="customizer.actTheme"
      :class="[
        customizer.actTheme,
        customizer.mini_sidebar ? 'mini-sidebar' : '',
        'verticalLayout',
      ]"
    >
      <ClientOnly>
        <!-- Móvil: menú inferior, igual al de Vendedor/Picker/Transportista -->
        <template v-if="mobile">
          <v-app-bar flat density="comfortable">
            <div class="d-flex align-center flex-grow-1" style="margin-left: 4px">
              <VImg :src="isDarkTheme ? LogoLight : Logo" height="40" width="123" style="flex: none" />
            </div>
            <div class="me-2">
              <LcFullVerticalHeaderThemeToggler />
            </div>
            <LcFullVerticalHeaderProfileDD />
          </v-app-bar>

          <v-main>
            <v-container fluid class="page-wrapper pb-sm-15 pb-10">
              <NuxtPage />
            </v-container>
          </v-main>

          <v-bottom-navigation class="tat-bottom-nav" :model-value="activeTab" grow>
            <v-btn v-for="tab in tabs" :key="tab.to" :value="tab.to" :to="tab.to">
              <v-icon>{{ tab.icon }}</v-icon>
              <span>{{ tab.title }}</span>
            </v-btn>
          </v-bottom-navigation>
        </template>

        <!-- Escritorio: sidebar lateral, como hoy -->
        <template v-else>
          <LcFullVerticalSidebar />
          <LcFullVerticalHeader />
          <v-main>
            <v-container fluid class="page-wrapper pb-sm-15 pb-10">
              <div :class="customizer.boxed ? 'maxWidth' : ''">
                <NuxtPage />
              </div>
            </v-container>
          </v-main>
        </template>
      </ClientOnly>
    </v-app>
  </v-locale-provider>
</template>

<style scoped>
.tat-bottom-nav {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
}
</style>
